import Button from '@/components/core/Button';
import Dropdown from '@/components/core/Dropdown';

import { list } from '@/graph/physicians';
import Box from '@material-ui/core/Box';
import MuiButton from '@material-ui/core/Button';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { format } from 'date-fns';
import React, { useCallback, useMemo } from 'react';
// TODO: Replace react-date-range Calendar with the shared MiniCalendar component
// (see ExecutiveAnalytics.tsx) for consistent theming across the app.
import { Calendar } from 'react-date-range';
import graphqlSelectInput from '../../../inputs/graphqlSelectInput';

export const PhysicianDropdown = graphqlSelectInput({
  entityName: 'Physician',
  placeholder: 'Physician',
  graphqlList: list,
  size: 'small',
  variant: 'outlined',
  sortOptions: true,
  getOptionValue: option => (option ? parseInt(option.value, 10) : undefined),
});

// Dark-theme overrides for react-date-range Calendar, scoped under .rdrDarkTheme.
// Styled to be visually consistent with the MiniCalendar in ExecutiveAnalytics.
const useDarkCalendarStyles = makeStyles({
  '@global': {
    '.rdrDarkTheme .rdrCalendarWrapper': {
      backgroundColor: '#152042',
      color: '#fff',
      borderRadius: 8,
    },
    '.rdrDarkThemeWithFooter .rdrCalendarWrapper': {
      borderRadius: '8px 8px 0 0',
    },
    '.rdrDarkTheme .rdrMonthAndYearWrapper': {
      paddingTop: 8,
    },
    '.rdrDarkTheme .rdrDayNumber span': {
      color: 'rgba(255, 255, 255, 0.7)',
      fontWeight: 500,
    },
    '.rdrDarkTheme .rdrDayPassive .rdrDayNumber span': {
      color: 'rgba(255, 255, 255, 0.15)',
    },
    '.rdrDarkTheme .rdrDayToday .rdrDayNumber span:after': {
      background: 'rgba(59, 130, 246, 0.5)',
    },
    '.rdrDarkTheme .rdrWeekDay': {
      color: 'rgba(255, 255, 255, 0.35)',
      fontWeight: 600,
      fontSize: '0.7rem',
      textTransform: 'uppercase',
    },
    '.rdrDarkTheme .rdrMonthAndYearPickers select': {
      color: 'rgba(255, 255, 255, 0.9)',
      backgroundColor: '#152042',
      fontWeight: 600,
    },
    '.rdrDarkTheme .rdrMonthAndYearPickers select option': {
      backgroundColor: '#152042',
    },
    '.rdrDarkTheme .rdrNextPrevButton': {
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
      borderRadius: 6,
    },
    '.rdrDarkTheme .rdrNextPrevButton:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.12)',
    },
    '.rdrDarkTheme .rdrPprevButton i': {
      borderColor: 'transparent rgba(255, 255, 255, 0.6) transparent transparent',
    },
    '.rdrDarkTheme .rdrNextButton i': {
      borderColor: 'transparent transparent transparent rgba(255, 255, 255, 0.6)',
    },
    '.rdrDarkTheme .rdrDay:not(.rdrDayPassive) .rdrSelected ~ .rdrDayNumber span': {
      color: '#fff',
      fontWeight: 700,
    },
    '.rdrDarkTheme .rdrDay:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span, .rdrDarkTheme .rdrDay:not(.rdrDayPassive) .rdrStartEdge ~ .rdrDayNumber span, .rdrDarkTheme .rdrDay:not(.rdrDayPassive) .rdrEndEdge ~ .rdrDayNumber span':
      {
        color: '#fff',
      },
    '.rdrDarkTheme .rdrDayDisabled': {
      backgroundColor: 'rgba(255, 255, 255, 0.03)',
    },
    '.rdrDarkTheme .rdrDayDisabled .rdrDayNumber span': {
      color: 'rgba(255, 255, 255, 0.12)',
    },
    '.rdrDarkTheme .rdrDay .rdrDayNumber span:hover': {
      color: '#fff',
    },
    '.rdrDarkTheme .rdrSelected': {
      backgroundColor: 'rgba(59, 130, 246, 0.4)',
    },
  },
});

const CalendarContent = ({ date, onSelect, onClear, close, isDark }) => {
  useDarkCalendarStyles();

  const handleChange = useCallback(
    d => {
      close();
      onSelect(d);
    },
    [close, onSelect]
  );
  const handleClear = useCallback(() => {
    close();
    onClear();
  }, [close, onClear]);
  return (
    <div className={isDark ? (onClear ? 'rdrDarkTheme rdrDarkThemeWithFooter' : 'rdrDarkTheme') : undefined}>
      <Calendar date={date} onChange={handleChange} />
      {onClear && (
        <Box
          display="flex"
          justifyContent="center"
          py={0.5}
          style={{
            backgroundColor: isDark ? '#152042' : undefined,
            borderTop: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.08)',
            borderRadius: isDark ? '0 0 8px 8px' : undefined,
          }}
        >
          <MuiButton
            size="small"
            onClick={handleClear}
            style={{ color: isDark ? 'rgba(255,255,255,0.6)' : undefined, textTransform: 'none', fontSize: '0.8rem' }}
          >
            Today (auto-follow)
          </MuiButton>
        </Box>
      )}
    </div>
  );
};

const Filters = ({ date, physician, withoutPhysician, onChange, onClearDate, buttonFormat }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';

  const onPhysicianChange = useCallback(value => onChange({ physician: value }), [onChange]);
  const onDateSelect = useCallback(d => onChange({ date: format(d, 'YYYY-MM-DD') }), [onChange]);
  const label = useMemo(() => format(date, buttonFormat || 'MMMM D, YYYY'), [date, buttonFormat]);

  return (
    <div className={classes.root}>
      {!withoutPhysician && (
        <Box minWidth={150}>
          <PhysicianDropdown onChange={onPhysicianChange} value={physician} menuWidthFollowContent />
        </Box>
      )}
      <Dropdown
        contentComponent={CalendarContent}
        contentProps={{ date, onSelect: onDateSelect, onClear: onClearDate, isDark }}
      >
        <Button variant="contained">{label}</Button>
      </Dropdown>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

export default Filters;
